import type {AccountInfo, AuthenticationResult, EventMessage} from '@azure/msal-browser';
import {EventType, InteractionRequiredAuthError, PublicClientApplication} from '@azure/msal-browser';
import {MsalProvider} from '@azure/msal-react';
import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import AuthError from '@/components/MSAL/AuthError';
import {msalConfig} from '@/components/MSAL/msalConfig';
import {absoluteUrl} from '@/utils/Uri';

export const msalInstance = new PublicClientApplication(msalConfig);

type MsalProviderContextInterface = {
    account : AccountInfo | null;
    logout : () => void;
    getAccessToken : (scope ?: string) => Promise<string>;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
const MsalContext = React.createContext<MsalProviderContextInterface>({});

const useMsalContext = () : MsalProviderContextInterface => {
    const context = React.useContext(MsalContext);

    if (Object.keys(context).length === 0) {
        throw new Error('useMsalProviderContext must be used within a MsalProviderContextProvider');
    }

    return context;
};

const apiScope : string = import.meta.env.VITE_APP_AAD_SCOPE;

const loginScopes = [
    'openid',
    apiScope,
];

const getToken = async (scope : string, account : AccountInfo) => {
    const scopes = [scope];

    try {
        return (await msalInstance.acquireTokenSilent({
            account,
            scopes,
            redirectUri: absoluteUrl('/silent.html').toString(),
        })).accessToken;
    } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect({account, scopes});
            return '';
        }
    }

    await msalInstance.loginRedirect({scopes});
    return '';
};

type Props = {children : React.ReactNode};

const MsalContextProvider = ({children} : Props) : JSX.Element => {
    const [account, setAccount] = useState<AccountInfo | null>(null);
    const [authError, setAuthError] = useState(false);

    const [refreshAccount] = useState<number>(0);

    useEffect(() => {
        if (account) {
            const interval = setInterval(() => {
                getToken(apiScope, account).catch(e => {
                    console.error('error fetching token', e);
                });
                //10 minutes. Only refreshes the token if it is expired.
            }, 600 * 1000);

            return () => {
                clearInterval(interval);
            };
        }
    }, [account, refreshAccount]);

    useEffect(() => {
        const initializeMsal = async () => {
            await msalInstance.initialize();

            const accounts = msalInstance.getAllAccounts();

            if (accounts.length > 0) {
                msalInstance.setActiveAccount(accounts[0]);
            }

            msalInstance.addEventCallback((event : EventMessage) => {
                if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                    const payload = event.payload as AuthenticationResult;
                    const account = payload.account;
                    msalInstance.setActiveAccount(account);
                }
            });

            let tokenResponse : AuthenticationResult | null = null;

            try {
                tokenResponse = await msalInstance.handleRedirectPromise();
            } catch (e) {
                setAuthError(true);
                return;
            }

            if (tokenResponse !== null) {
                setAccount(tokenResponse.account);
                return;
            }

            // Step 2: Check cached data
            const currentAccounts = msalInstance.getAllAccounts();

            if (currentAccounts.length > 0) {
                setAccount(currentAccounts[0]);
                return;
            }
        };

        void initializeMsal();
    }, []);

    const value = useMemo(() => ({
        account: account,
        logout: async () => {
            await msalInstance.logout();
        },
        getAccessToken: async (scope = apiScope) => {
            if (account === null) return '';
            return await getToken(scope, account);
        },
    }), [account]);

    if (authError) {
        return <AuthError login={async () => msalInstance.loginRedirect({scopes: loginScopes})}/>;
    }

    return (
        <MsalContext.Provider value={value}>
            <MsalProvider instance={msalInstance}>
                {children}
            </MsalProvider>
        </MsalContext.Provider>
    );
};

export {MsalContextProvider, useMsalContext};
